/**
 * 파일명 : hom0201m.jsx
 * 설명 : 프로젝트매핑 탭 사내인트라넷개발 목록 조회 화면
 * 수정내역
 ******************************************************
 * Version    수정일자      수정자              수정내용      
 ******************************************************
 * 1.0       2023-05-12   장용, 황은택          최초작성
 * 1.1       2024-03-13   이경원,백승준,기라성  페이징처리 오류 수정, 엔터키조회 추가
 */

/************************************************
 * Import 영역  
 ************************************************/
/* React */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Hom0201mAction } from '../../../redux/slice/hom/hom0201m_slice';
import { IInquiryCndsState, IProjectState } from '../../../redux/models/hom/hom0201m_model';

/* AntD */
import { Button, Space, Input, Row, Col, Layout, Typography, Collapse, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import { Pjt0102mAction } from 'redux/slice/pjt/pjt0102m_slice';

/************************************************
 * 전역변수 영역
 ************************************************/
const Hom0201m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const inquiryCnds  = useAppSelector((state) => state.Hom0201m.inquiryCnds);
    const projectList = useAppSelector((state) => state.Hom0201m.projectList);
    const current      = useAppSelector((state) => state.Hom0201m.current);
    const pageSize     = useAppSelector((state) => state.Hom0201m.pageSize);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
     React.useEffect(() => {
        fn_Search();
    }, []);

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>번호</div>,
                dataIndex: 'no',
                key: 'no',
                align: 'center',
                width: '5%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트명</div>,
                dataIndex: 'pjNm',
                key: 'pjNm',
                align: 'left',
                width: '25%',
                render: (text:string) => text.trim()
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트내용</div>,
                dataIndex: 'pjCntnt',
                key: 'pjCntnt',
                align: 'left',
                width: '45%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>기관명</div>,
                dataIndex: 'cusNm',
                key: 'cusNm',
                align: 'center',
                width: '15%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>매핑여부</div>,
                dataIndex: 'mappingYn',
                key: 'mappingYn',
                align: 'center',            
                width: '8%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>매핑</div>,
                key: 'action',
                render: (record:IProjectState) => (
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={(e) => onClick(e, record)}
                    >
                        매핑
                    </Button>
                ),
                align: 'center',
                width: '6%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}></div>,
                dataIndex: 'id',
                key: 'id',
                align: 'center',            
                width: '0%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}></div>,
                dataIndex: 'prjMappingId',
                key: 'prjMappingId',
                align: 'center',            
                width: '0%',
            }];
            return arr;
    }, [projectList]);


    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Hom0201mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Hom0201mAction.setProjectList([]));
    };

    /**
     * 기능 : 검색 입력값 변경 이벤트
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const inputOnChange = React.useCallback((e:any) => {
        const newItem = {...inquiryCnds, [e.currentTarget.name] : e.currentTarget.value};
        dispatch(Hom0201mAction.setInquiryCnds(newItem));
    }, [inquiryCnds]);

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IProjectState) => {
        const buttonName = e.currentTarget.name;
        const width = '100%';
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            case 'btn_showDetail': {
                const params:any = {};
                params.title = '사업장매핑';
                params.popupId = 'hom0201p';
                params.accept = fn_Search;
                params.width = 1100;
                params.props = {
                    id : record.id,
                    prjMappingId : record.prjMappingId,
                };
                common.openPopup(params);
                break;
            }

            default:
        }
    };
    
    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        fn_ResetResult();
        dispatch(Hom0201mAction.setCurrent(1));

        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRHOMR0201',
            success: (responseData:any) => {
                dispatch(Hom0201mAction.setProjectList(responseData.projectList));
            },
        });
    };
    
    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };

    return (
        <>
            <Layout>
                <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Col flex='9.5'>
                                    <Space>
                                        <Col>
                                            <Space>
                                                <label>기관명</label>
                                                <Input
                                                    name='conditionCusnm'
                                                    placeholder='입력하세요'
                                                    style={{ width: 180 }}
                                                    value={inquiryCnds.conditionCusnm}
                                                    onChange={inputOnChange}
                                                    onKeyUp={handleOnKeyUp}
                                                />
                                            </Space>
                                        </Col>
                                        <Col>
                                            <Space>
                                                <label>프로젝트명</label>   
                                                <Input
                                                    name='conditionPjNm'
                                                    placeholder='입력하세요'
                                                    style={{ width: 500 }}
                                                    value={inquiryCnds.conditionPjNm}
                                                    onChange={inputOnChange}
                                                    onKeyUp={handleOnKeyUp}
                                                />
                                            </Space>
                                        </Col>
                                    </Space>
                                </Col>
                                <Col flex='0.5'>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            초기화
                                    </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            조회
                                    </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>프로젝트 목록</Text>
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        dataSource={projectList}
                        key='no'
                        columns={columns().filter((col:any) => col.dataIndex !== 'id' && col.dataIndex !== 'prjMappingId')}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + projectList.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Hom0201mAction.setCurrent(page));
                                dispatch(Hom0201mAction.setPageSize(pageSize));
                            },
                        }}
                    />
                </Content>
            </Layout>
        </>
    );
};

export default Hom0201m;